import { Box, Paper, Stack, Typography, Chip, Button, Avatar } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  WarningAmberOutlined,
  SupportAgentOutlined,
  PersonOutlineOutlined,
} from "@mui/icons-material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ProfileModal from "../../../componentes/topBar/profile/profileModal";

function getGreeting(hour) {
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
}

function initials(name) {
  const parts = (name || "").trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function HomeHeader({ nome = "", foto = "", suspended = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [openProfile, setOpenProfile] = useState(false);
  const now = new Date();
  const greeting = getGreeting(now.getHours());
  const dateLabel = now.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const firstName = (nome || "").trim().split(" ")[0];

  const primary = theme.palette.primary.main;
  const isLight = theme.palette.mode === "light";

  const styles = {
    // Mesmo banner/layout, porém com paleta discreta: leve tint da marca sobre
    // o tema, texto nas cores normais — não rouba a atenção.
    paper: {
      position: "relative",
      overflow: "hidden",
      borderRadius: 2.5,
      p: { xs: 2.5, sm: 3 },
      color: theme.palette.text.primary,
      background: isLight
        ? theme.palette.background.paper
        : `linear-gradient(120deg, ${alpha(primary, 0.1)} 0%, ${alpha(
            primary,
            0.03,
          )} 100%)`,
      boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
    },
    glow: {
      position: "absolute",
      borderRadius: "50%",
      background: alpha(primary, 0.06),
      filter: "blur(2px)",
    },
    avatar: {
      width: 52,
      height: 52,
      fontSize: "1.25rem",
      fontWeight: 600,
      bgcolor: alpha(primary, 0.16),
      color: primary,
    },
    date: {
      textTransform: "capitalize",
      color: theme.palette.text.secondary,
      fontSize: "0.85rem",
      fontWeight: 400,
    },
    greeting: {
      fontSize: { xs: "1.35rem", sm: "1.65rem" },
      fontWeight: 600,
      lineHeight: 1.15,
      color: theme.palette.text.primary,
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: "0.92rem",
      mt: 0.5,
    },
    actionBtn: {
      color: theme.palette.text.secondary,
      borderColor: theme.palette.stroke?.main || theme.palette.divider,
      backgroundColor: theme.palette.background.paper,
      textTransform: "none",
      borderRadius: 1.5,
      px: 2,
      "&:hover": {
        borderColor: primary,
        color: primary,
        backgroundColor: alpha(primary, 0.06),
      },
    },
  };

  return (
    <>
    <Paper sx={styles.paper} elevation={0}>
      <Box sx={{ ...styles.glow, width: 260, height: 260, top: -110, right: -50 }} />
      <Box sx={{ ...styles.glow, width: 150, height: 150, bottom: -80, right: 150 }} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={2.5}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Stack direction="row" alignItems="center" gap={2.5}>
          <Avatar src={foto || undefined} sx={styles.avatar}>
            {initials(nome)}
          </Avatar>
          <Box>
            <Typography sx={styles.date}>{dateLabel}</Typography>
            <Typography sx={styles.greeting}>
              {greeting}
              {firstName ? `, ${firstName}` : ""}!
            </Typography>
            <Typography sx={styles.subtitle}>
              Que bom te ver por aqui. Este é o resumo do seu ambiente.
            </Typography>
            {suspended && (
              <Chip
                icon={<WarningAmberOutlined />}
                label="Sua conta está suspensa — fale com o suporte"
                size="small"
                color="error"
                sx={{ mt: 1.5, fontWeight: 500 }}
              />
            )}
          </Box>
        </Stack>

        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={<PersonOutlineOutlined />}
            sx={styles.actionBtn}
            onClick={() => setOpenProfile(true)}
          >
            Meu perfil
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SupportAgentOutlined />}
            sx={styles.actionBtn}
            href="https://wa.me/5584994581475"
            target="_blank"
            rel="noopener noreferrer"
          >
            Suporte
          </Button>
        </Stack>
      </Stack>
    </Paper>

      <ProfileModal openModal={openProfile} setOpenModal={setOpenProfile} />
    </>
  );
}
